import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import QRCode from "../components/QRCode";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { WalletPass } from "../components/WalletPass";
import { LogoUpload } from "../components/LogoUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Plus, ExternalLink, Pause, Play, Smartphone, Printer, Link as LinkIcon, Copy, Palette, Settings2, Info, Share2, RotateCw } from "lucide-react";
import { PosterModal } from "../components/PosterModal";
import { PhoneWithCountry } from "../components/PhoneWithCountry";
import { DistributePanel } from "../components/DistributePanel";

export default function Programs() {
  const { activeOrg, user } = useAuth();
  const isSuperadmin = !!user?.is_superadmin;
  const [progs, setProgs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [wallet, setWallet] = useState("apple");
  const [posterFor, setPosterFor] = useState(null);
  const [tab, setTab] = useState("basics");   // basics · branding · back · distribute
  const [face, setFace] = useState("front");   // preview face: front | back
  const [justLive, setJustLive] = useState(null); // program that just went live — shows the scan-now modal

  const load = () => activeOrg && api.get(`/orgs/${activeOrg.id}/programs`).then((r) => setProgs(r.data));
  useEffect(() => { load(); }, [activeOrg]);
  if (!activeOrg) return null;

  const blank = { name: "New Program", type: "visit", pass_title: "Loyalty Card", brand_color: "#0B0D10", secondary_color: "#C8FF4D", text_color: "#FFFFFF", terms: "Standard terms apply.", points_per_dollar: 1, barcode_label: "Member ID", logo_url: "", hero_url: "", website_url: "", support_email: "", support_phone: "", about_note: "", redeem_pin_required: false, reward: { name: "Free reward", threshold: 10, trigger_type: "visits", description: "", resets_after_redeem: true } };

  const copyJoinLink = async (p) => {
    try {
      // window.location.origin (not REACT_APP_BACKEND_URL) so the link always
      // encodes whatever domain the owner is on — when they deploy to their
      // real domain (e.g. backpocketloyalty.com), the QR shows their brand.
      await navigator.clipboard.writeText(`${window.location.origin}/api/j/${activeOrg.slug}/${p.slug}?src=share`);
      toast.success("Join link copied");
    } catch (_) { toast.error("Copy failed"); }
  };

  const save = async () => {
    try {
      // Enforce the "stamps-to-unlock max 10" rule client-side too so the merchant
      // gets an inline error instead of a server-side 400.
      const thresh = parseInt(editing?.reward?.threshold, 10) || 1;
      if (editing?.type === "stamp" && thresh > 10) {
        toast.error("Stamp cards max out at 10 stamps to keep the pass beautiful and quick to redeem.");
        return;
      }
      if (creating) {
        const { data } = await api.post(`/orgs/${activeOrg.id}/programs`, editing);
        toast.success("Program created");
        setEditing(null); setCreating(false); setTab("basics"); setFace("front");
      } else {
        await api.patch(`/orgs/${activeOrg.id}/programs/${editing.id}`, editing);
        toast.success("Saved");
        setEditing(null); setTab("basics"); setFace("front");
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const publish = async (p) => {
    try {
      await api.post(`/orgs/${activeOrg.id}/programs/${p.id}/publish`);
      load();
      if (editing) { setEditing(null); setCreating(false); setTab("basics"); setFace("front"); }
      setJustLive({ ...p, status: "live" });
    } catch (e) { toast.error(apiError(e)); }
  };
  // Publish straight from the editor: save any pending edits first, then go
  // live and pop the scan-now QR so the founder can add it to their wallet
  // on the spot.
  const publishFromEditor = async () => {
    try {
      await api.patch(`/orgs/${activeOrg.id}/programs/${editing.id}`, editing);
      await api.post(`/orgs/${activeOrg.id}/programs/${editing.id}/publish`);
      const p = { ...editing, status: "live" };
      setEditing(null); setCreating(false); setTab("basics"); setFace("front");
      load();
      setJustLive(p);
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (p) => { const s = p.status === "paused" ? "live" : "paused"; await api.post(`/orgs/${activeOrg.id}/programs/${p.id}/status`, { status: s }); load(); };

  if (editing) {
    // window.location.origin so the QR + poster + link inherit the owner's real
    // domain the moment they deploy — never emits an emergent preview URL.
    const joinUrl = `${window.location.origin}/api/j/${activeOrg.slug}/${editing.slug || ""}`;
    const set = (patch) => setEditing({ ...editing, ...patch });
    const setReward = (patch) => setEditing({ ...editing, reward: { ...editing.reward, ...patch } });
    const ALL_TABS = [
      { id: "basics", label: "Basics", icon: Settings2 },
      { id: "branding", label: "Branding", icon: Palette },
      { id: "back", label: "Back of pass", icon: Info },
      { id: "distribute", label: "Distribute", icon: Share2 },
    ];
    // Done-For-You: merchants only see Distribute. Superadmin sees everything so
    // the BackPocket team can still design/edit the pass for the merchant.
    const TABS = isSuperadmin ? ALL_TABS : ALL_TABS.filter((t) => t.id === "distribute");
    // If a merchant somehow landed on a config tab, force them to Distribute.
    const effectiveTab = TABS.some((t) => t.id === tab) ? tab : "distribute";
    return (
      <div>
        <PageHeader title={creating ? "Create program" : editing.name} subtitle={isSuperadmin ? "Design your pass, edit the back, and share it. Every change updates the live preview →" : "Your live loyalty pass and every way to get customers on it."}
          action={<div className="flex gap-2"><Button variant="outline" className="rounded-full" onClick={() => { setEditing(null); setCreating(false); setTab("basics"); setFace("front"); }}>Back</Button>{isSuperadmin && !creating && editing.status === "draft" && <Button data-testid="program-publish" onClick={publishFromEditor} className="btn-lift bg-[#C8FF4D] text-[#0B0D10] hover:bg-[#b8ef3d] rounded-full">Publish</Button>}{isSuperadmin && <Button data-testid="program-save" onClick={save} className="btn-lift bg-[#0B0D10] text-white rounded-full">{creating ? "Create draft" : "Save"}</Button>}</div>} />
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          {/* LEFT — tabbed editor form (Distribute-only for merchants; full 4-tab for superadmin) */}
          <div>
            {TABS.length > 1 && (
              <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-full bg-[#F0EDE5] border border-[#E8E5DE] w-fit" role="tablist">
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button key={id} onClick={() => setTab(id)} role="tab" aria-selected={effectiveTab === id} data-testid={`tab-${id}`}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${effectiveTab === id ? "bg-white text-[#0B0D10] shadow-sm" : "text-[#25282E]/60 hover:text-[#0B0D10]"}`}>
                    <Icon size={13} />{label}
                  </button>
                ))}
              </div>
            )}

            {!isSuperadmin && effectiveTab === "distribute" && editing.status === "live" && (
              <div className="rounded-2xl bg-[#F4F1E9] border border-[#E8E5DE] p-3 mb-5 flex items-start gap-2.5 text-[12.5px] text-[#0B0D10]/85" data-testid="dfy-banner">
                <Info size={14} className="mt-0.5 shrink-0 text-[#0B0D10]/70" />
                <div>Your BackPocket concierge designed this pass. Need changes? Email <a href="mailto:hello@backpocketloyalty.com" className="font-medium underline">hello@backpocketloyalty.com</a> and we'll ship it inside 24 hours.</div>
              </div>
            )}

            {effectiveTab === "basics" && isSuperadmin && (
              <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6 space-y-4" data-testid="pane-basics">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50">Pass details</div>
                <div><Label>Program name</Label><Input className="mt-1.5 bg-white rounded-xl" value={editing.name} onChange={(e) => set({ name: e.target.value })} data-testid="program-name" placeholder="e.g. Coffee Club" /></div>
                <div><Label>Type</Label>
                  <select className="mt-1.5 w-full h-11 rounded-xl border border-[#E8E5DE] bg-white px-3 text-sm" value={editing.type} onChange={(e) => set({ type: e.target.value })}>
                    {[["visit", "Visits — reward after N visits"], ["points", "Points — earn per dollar"], ["membership", "Membership — VIP tier"]].concat(editing.type === "stamp" ? [["stamp", "Stamp card — stamp every visit (legacy)"]] : []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div><Label>Pass title <span className="text-[#25282E]/40 text-xs font-normal">(shown on the pass front)</span></Label><Input className="mt-1.5 bg-white rounded-xl" value={editing.pass_title || ""} onChange={(e) => set({ pass_title: e.target.value })} placeholder="Loyalty Card" /></div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div><Label>Reward name</Label><Input className="mt-1.5 bg-white rounded-xl" value={editing.reward.name} onChange={(e) => setReward({ name: e.target.value })} placeholder="Free coffee" /></div>
                  <div>
                    <Label>{editing.type === "points" ? "Points to unlock" : editing.type === "stamp" ? "Stamps to unlock" : "Visits to unlock"}
                      {editing.type === "stamp" && <span className="text-[#25282E]/40 text-xs font-normal ml-1">(max 10)</span>}
                    </Label>
                    <Input
                      type="number"
                      min={1}
                      max={editing.type === "stamp" ? 10 : 1000}
                      className="mt-1.5 bg-white rounded-xl"
                      value={editing.reward.threshold}
                      onChange={(e) => {
                        const raw = parseInt(e.target.value) || 1;
                        const cap = editing.type === "stamp" ? 10 : 1000;
                        setReward({ threshold: Math.min(Math.max(1, raw), cap) });
                      }}
                    />
                  </div>
                </div>
                <label className="mt-5 flex items-start gap-3.5 rounded-2xl border border-[#E8E5DE] bg-[#FBF9F4] p-4 cursor-pointer" data-testid="prog-pin-toggle-row">
                  <Switch checked={!!editing.redeem_pin_required} onCheckedChange={(v) => set({ redeem_pin_required: v })} data-testid="prog-pin-toggle" className="mt-0.5" />
                  <span className="min-w-0">
                    <span className="block font-semibold text-[13.5px]">Require a customer PIN to redeem</span>
                    <span className="block text-[12px] text-[#25282E]/60 mt-0.5 leading-relaxed">For high-value rewards. Customers set a 4-digit PIN from their pass page; staff can't redeem without it. Five wrong tries locks the pass for 15 minutes.</span>
                  </span>
                </label>
              </div>
            )}

            {effectiveTab === "branding" && isSuperadmin && (
              <div className="space-y-5" data-testid="pane-branding">
                <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6 space-y-5">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50">Brand images</div>
                  <LogoUpload value={editing.logo_url} onChange={(url) => set({ logo_url: url })} label="Business logo"
                    help="Appears as the pass icon and in every push notification. Square PNG or JPG, transparent bg looks best." size={64} testId="prog-logo-upload" />
                  <LogoUpload value={editing.hero_url} onChange={(url) => set({ hero_url: url })} label="Cover image (optional)" aspect="strip" maxPx={900}
                    help="Wide banner behind the pass fields — a shop front, hero drink, hero cut. 3:1 landscape works best." size={44} testId="prog-hero-upload" />
                </div>
                <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6 space-y-4">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50">Colours</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Brand</Label><input type="color" className="mt-1.5 w-full h-11 rounded-xl border cursor-pointer" value={editing.brand_color} onChange={(e) => set({ brand_color: e.target.value })} data-testid="brand-color" /><div className="mt-1 text-[11px] text-[#25282E]/50 font-mono">{editing.brand_color?.toUpperCase()}</div></div>
                    <div><Label>Accent</Label><input type="color" className="mt-1.5 w-full h-11 rounded-xl border cursor-pointer" value={editing.secondary_color} onChange={(e) => set({ secondary_color: e.target.value })} /><div className="mt-1 text-[11px] text-[#25282E]/50 font-mono">{editing.secondary_color?.toUpperCase()}</div></div>
                    <div><Label>Text</Label><input type="color" className="mt-1.5 w-full h-11 rounded-xl border cursor-pointer" value={editing.text_color} onChange={(e) => set({ text_color: e.target.value })} /><div className="mt-1 text-[11px] text-[#25282E]/50 font-mono">{editing.text_color?.toUpperCase()}</div></div>
                  </div>
                  <div className="pt-1 flex flex-wrap gap-2">
                    {[
                      { label: "Ink", bc: "#0B0D10", ac: "#C8FF4D", tc: "#FFFFFF" },
                      { label: "Cream", bc: "#F4F1E9", ac: "#0B0D10", tc: "#0B0D10" },
                      { label: "Midnight", bc: "#14202E", ac: "#6ED0FF", tc: "#FFFFFF" },
                      { label: "Wine", bc: "#2A1522", ac: "#FF9ECF", tc: "#FFFFFF" },
                      { label: "Forest", bc: "#152118", ac: "#7BE495", tc: "#FFFFFF" },
                      { label: "Ember", bc: "#231108", ac: "#FF9F5C", tc: "#FFFFFF" },
                    ].map((p) => (
                      <button key={p.label} data-testid={`palette-${p.label.toLowerCase()}`} onClick={() => set({ brand_color: p.bc, secondary_color: p.ac, text_color: p.tc })}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E8E5DE] hover:border-[#0B0D10] px-2.5 py-1 text-[11.5px] transition-colors">
                        <span className="w-3.5 h-3.5 rounded-full ring-1 ring-black/10" style={{ background: p.bc }} />
                        <span className="w-3.5 h-3.5 rounded-full -ml-2 ring-1 ring-black/10" style={{ background: p.ac }} />
                        <span className="ml-1">{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {effectiveTab === "back" && isSuperadmin && (
              <div className="rounded-3xl bg-white border border-[#E8E5DE] p-6 space-y-4" data-testid="pane-back">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[#25282E]/50">Back of pass</div>
                  <button onClick={() => setFace(face === "front" ? "back" : "front")} className="text-[11.5px] text-[#25282E]/60 hover:text-[#0B0D10] inline-flex items-center gap-1"><RotateCw size={12} />Flip preview to {face === "front" ? "back" : "front"}</button>
                </div>
                <div><Label>About your business <span className="text-[#25282E]/40 text-xs font-normal">(the story, house rules, opening hours)</span></Label>
                  <textarea rows={3} className="mt-1.5 w-full rounded-xl border border-[#E8E5DE] bg-white p-3 text-sm resize-y" value={editing.about_note || ""} onChange={(e) => set({ about_note: e.target.value })} placeholder="Small-batch coffee, roasted round the corner. Open every day 6am–4pm." data-testid="about-note" />
                </div>
                <div><Label>Terms</Label>
                  <textarea rows={2} className="mt-1.5 w-full rounded-xl border border-[#E8E5DE] bg-white p-3 text-sm resize-y" value={editing.terms || ""} onChange={(e) => set({ terms: e.target.value })} placeholder="One stamp per visit. Non-transferable." data-testid="terms" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Website <span className="text-[#25282E]/40 text-xs font-normal">(tappable)</span></Label><Input type="url" className="mt-1.5 bg-white rounded-xl" value={editing.website_url || ""} onChange={(e) => set({ website_url: e.target.value })} placeholder="https://oracoffee.com" /></div>
                  <div>
                    <Label>Phone</Label>
                    <PhoneWithCountry value={editing.support_phone || ""} onChange={(v) => set({ support_phone: v })} />
                  </div>
                </div>
                <div><Label>Support email</Label><Input type="email" className="mt-1.5 bg-white rounded-xl" value={editing.support_email || ""} onChange={(e) => set({ support_email: e.target.value })} placeholder="hello@oracoffee.com" /></div>
                <div className="text-[12px] text-[#25282E]/55 leading-relaxed border-t border-[#F0EDE5] pt-4 mt-2">
                  These fields sit on the back of the pass — customers see them when they tap the <span className="font-mono px-1.5 py-0.5 rounded bg-[#F0EDE5] text-[11px]">•••</span> icon in Apple Wallet. Website, phone and email render as native tappable rows.
                </div>
              </div>
            )}

            {effectiveTab === "distribute" && !creating && (
              <DistributePanel
                program={editing}
                orgId={activeOrg.id}
                orgName={activeOrg.name}
                orgSlug={activeOrg.slug}
                joinUrl={joinUrl}
                onEditPoster={(prog, size) => setPosterFor({ program: prog, size })}
              />
            )}

            {effectiveTab === "distribute" && creating && (
              <div className="rounded-3xl border-2 border-dashed border-[#E8E5DE] p-6 text-center text-sm text-[#25282E]/60" data-testid="pane-distribute-empty">
                Distribution options will unlock as soon as you save this draft.
              </div>
            )}
          </div>

          {/* RIGHT — sticky live preview with wallet toggle + tap-to-flip */}
          <div className="lg:sticky lg:top-8 self-start">
            <div className="flex justify-center gap-2 mb-4">
              <button onClick={() => setWallet("apple")} data-testid="preview-apple" className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${wallet === "apple" ? "bg-[#0B0D10] text-white" : "bg-white border border-[#E8E5DE] hover:border-[#0B0D10]"}`}><Smartphone size={12} className="inline mr-1" />Apple</button>
              <button onClick={() => setWallet("google")} data-testid="preview-google" className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${wallet === "google" ? "bg-[#0B0D10] text-white" : "bg-white border border-[#E8E5DE] hover:border-[#0B0D10]"}`}>Google</button>
            </div>
            <div className="flex justify-center gap-1 mb-5 p-1 rounded-full bg-[#F0EDE5] border border-[#E8E5DE] mx-auto w-fit">
              {["front", "back"].map((f) => (
                <button key={f} onClick={() => setFace(f)} data-testid={`face-${f}`}
                  className={`px-3.5 py-1 rounded-full text-[12px] font-medium capitalize transition-colors ${face === f ? "bg-white text-[#0B0D10] shadow-sm" : "text-[#25282E]/55 hover:text-[#0B0D10]"}`}>{f}</button>
              ))}
            </div>
            <div className="relative flex justify-center py-6">
              <div className="absolute inset-0 rounded-[32px] bg-gradient-to-tr from-[#6474FF]/8 to-[#C8FF4D]/16 blur-2xl" />
              <div className="relative">
                <WalletPass program={{ ...editing, org_name: activeOrg.name }} wallet={wallet} state={{ balance: 4 }} face={face} onFlip={setFace} />
              </div>
            </div>
            <p className="mt-1 text-center text-[11px] uppercase tracking-[0.14em] text-[#25282E]/40">Tap the pass to flip · live preview</p>
          </div>
        </div>
        <PosterModal open={!!posterFor} program={posterFor?.program || posterFor} size={posterFor?.size} orgName={activeOrg.name} orgSlug={activeOrg.slug} onClose={() => setPosterFor(null)} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Programs" subtitle={isSuperadmin ? "Your loyalty programs and wallet passes." : "Your live loyalty pass and every way to get customers on it."}
        action={isSuperadmin ? <Button data-testid="new-program-btn" onClick={() => { setEditing(blank); setCreating(true); }} className="btn-lift bg-[#C8FF4D] text-[#0B0D10] hover:bg-[#b8ef3d] rounded-full font-semibold"><Plus size={16} className="mr-1" />New program</Button> : null} />
      {progs.length === 0 ? (
        isSuperadmin ? (
          <div className="rounded-3xl border-2 border-dashed border-[#E8E5DE] p-16 text-center bg-white/40">
            <h3 className="font-display text-2xl font-extrabold tracking-[-0.01em]">Create something worth coming back for.</h3>
            <p className="text-[#25282E]/70 mt-2">Your first loyalty program is minutes away.</p>
            <Button onClick={() => { setEditing(blank); setCreating(true); }} className="btn-lift mt-6 bg-[#0B0D10] text-white rounded-full font-semibold"><Plus size={16} className="mr-1" />New program</Button>
          </div>
        ) : (
          <div className="rounded-3xl bg-[#0B0D10] text-white p-10 relative overflow-hidden grain" data-testid="dfy-empty">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#C8FF4D]/15 blur-3xl" />
            <div className="relative z-10 max-w-xl">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#C8FF4D] text-[#0B0D10] px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.14em]">Concierge setup in progress</div>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-[-0.01em]">Your pass is being built.</h3>
              <p className="mt-2 text-white/70 text-[14px] leading-relaxed">A real human on the BackPocket team is designing your loyalty pass, wiring your rewards and printing your counter QR. You'll see the finished pass before it goes live — usually inside 48 hours.</p>
              <div className="mt-6 rounded-2xl bg-white/[0.05] border border-white/10 p-4 text-[13px] text-white/80">
                Questions before your handover call? Email <a href="mailto:hello@backpocketloyalty.com" className="text-[#C8FF4D] font-medium">hello@backpocketloyalty.com</a>.
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {progs.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="group rounded-3xl bg-white border border-[#E8E5DE] p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_48px_-30px_rgba(11,13,16,0.45)]" data-testid={`program-card-${p.id}`}>
              <div className="rounded-2xl overflow-hidden mb-4 flex justify-center scale-75 origin-top h-[190px] bg-gradient-to-br from-[#F6F3EC] to-[#EFEBE1]"><WalletPass program={{ ...p, org_name: activeOrg.name }} state={{ balance: 4 }} /></div>
              <div className="flex items-center justify-between">
                <div className="min-w-0"><h3 className="font-display font-bold text-lg truncate">{p.name}</h3><span className="text-xs text-[#25282E]/60 capitalize">{p.type} program</span></div>
                <span className={`text-[11px] px-2.5 py-0.5 rounded-full capitalize shrink-0 ${p.status === "live" ? "bg-[#C8FF4D] text-[#0B0D10] font-medium" : "bg-[#EFEBE1] text-[#25282E]"}`}>{p.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-[#F0EDE5] text-center">
                {[["issued", p.stats?.issued || 0], ["installs", p.stats?.installed || 0], ["redeemed", p.stats?.redeemed || 0]].map(([l, v]) => (
                  <div key={l}><div className="font-display font-extrabold text-xl tracking-[-0.01em]">{v}</div><div className="text-[10px] uppercase tracking-[0.1em] text-[#25282E]/50 mt-0.5">{l}</div></div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <Button variant="outline" className="flex-1 rounded-full h-9 text-xs" onClick={() => { setTab(isSuperadmin ? "basics" : "distribute"); setEditing(p); }} data-testid={`edit-program-${p.id}`}>{isSuperadmin ? "Edit" : "Manage"}</Button>
                {p.status === "live" && (
                  <>
                    <Button variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => copyJoinLink(p)} title="Copy join link" data-testid={`copy-link-${p.id}`}><LinkIcon size={14} /></Button>
                    <Button className="rounded-full h-9 px-3 text-xs bg-[#0B0D10] text-white hover:bg-black" onClick={() => setPosterFor(p)} data-testid={`poster-${p.id}`}><Printer size={13} className="mr-1" />Poster</Button>
                    {isSuperadmin && <Button variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => toggle(p)} title="Pause"><Pause size={14} /></Button>}
                  </>
                )}
                {isSuperadmin && p.status === "paused" && <Button variant="outline" className="rounded-full h-9 w-9 p-0" onClick={() => toggle(p)}><Play size={14} /></Button>}
                {isSuperadmin && p.status === "draft" && <Button className="rounded-full h-9 text-xs bg-[#0B0D10] text-white" onClick={() => publish(p)}>Publish</Button>}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <PosterModal open={!!posterFor} program={posterFor?.program || posterFor} size={posterFor?.size} orgName={activeOrg.name} orgSlug={activeOrg.slug} onClose={() => setPosterFor(null)} />
      <LiveScanModal program={justLive} orgSlug={activeOrg.slug} onClose={() => setJustLive(null)} />
    </div>
  );
}

// Shown the moment a pass goes live — a big scannable QR so the founder can
// point their phone at the screen and have the pass in their wallet in
// seconds. Encodes /api/j/{org}/{program}: iPhone streams the .pkpass
// directly, everything else lands on the join page.
function LiveScanModal({ program, orgSlug, onClose }) {
  if (!program) return null;
  const url = `${window.location.origin}/api/j/${orgSlug}/${program.slug}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch (_) {}
  };
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center p-4 bg-[#0B0D10]/70 backdrop-blur-sm" onClick={onClose} data-testid="live-scan-modal">
      <motion.div initial={{ opacity: 0, y: 18, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[420px] bg-[#0B0D10] text-white rounded-[28px] border border-white/10 p-8 text-center relative overflow-hidden grain">
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#C8FF4D]/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] text-[#C8FF4D] font-bold mb-3">
            <span className="h-1.5 w-1.5 rounded-full bg-[#C8FF4D] animate-pulse" />Live
          </div>
          <h3 className="font-display font-extrabold text-[26px] tracking-[-0.02em] leading-tight">{program.name} is live.</h3>
          <p className="text-white/60 text-[13.5px] mt-2 leading-relaxed">Point a phone camera at the code — the wallet install sheet opens instantly.</p>
          <div className="mx-auto mt-6 p-4 bg-white rounded-2xl inline-block" data-testid="live-scan-qr">
            <QRCode value={url} size={220} />
          </div>
          <div className="mt-4 rounded-lg bg-white/[0.05] border border-white/10 px-3 py-2 text-[11.5px] font-mono text-white/60 truncate">{url}</div>
          <div className="mt-5 flex items-center justify-center gap-2.5">
            <button onClick={copy} data-testid="live-scan-copy"
              className="btn-lift inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] border border-white/15 hover:bg-white/[0.15] px-4 h-10 text-[13px] font-semibold">
              <Copy size={13} />Copy link
            </button>
            <button onClick={onClose} data-testid="live-scan-done"
              className="btn-lift rounded-full bg-[#C8FF4D] text-[#0B0D10] px-5 h-10 text-[13px] font-semibold hover:bg-[#b8ef3d]">
              Done
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
